import {
  AlertTriangle, Zap, CheckCircle, Shield,
  Stethoscope, Eye, Target, Info, GitCompareArrows
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

export const ReportSection = ({ title, icon, items = [], emptyText, variant }) => {
  const variantStyles = {
    critical: 'bg-red-500/5 border-red-500/10',
    high: 'bg-orange-500/5 border-orange-500/10',
    normal: 'bg-green-500/5 border-green-500/10',
  };

  const bulletColor = {
    critical: 'text-red-400',
    high: 'text-orange-400',
    normal: 'text-green-400',
  };

  return (
    <div className={`p-4 rounded-lg border ${variantStyles[variant] || 'bg-[hsl(210,14%,12%)] border-border'}`}>
      <h4 className="text-xs font-semibold flex items-center gap-2 mb-3">
        {icon}
        {title}
      </h4>
      {items && items.length > 0 ? (
        <div className="space-y-1.5">
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`mt-1 ${bulletColor[variant] || 'text-muted-foreground'}`}>•</span>
              <p className="text-xs text-foreground/80 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic">{emptyText}</p>
      )}
    </div>
  );
};

export const AnomalyCard = ({ anomaly, t }) => {
  if (typeof anomaly === 'string') {
    return (
      <div className="p-3 rounded-lg bg-[hsl(210,14%,12%)] border border-border">
        <p className="text-xs text-foreground/80">{anomaly}</p>
      </div>
    );
  }

  const severityColors = {
    CRITICAL: 'text-red-400 bg-red-500/10',
    HIGH: 'text-orange-400 bg-orange-500/10',
    MODERATE: 'text-yellow-400 bg-yellow-500/10',
    LOW: 'text-green-400 bg-green-500/10',
  };

  return (
    <div className="p-4 rounded-lg bg-[hsl(210,14%,12%)] border border-border">
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-sm font-medium text-foreground/90">{anomaly.description}</p>
        {anomaly.severity && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${severityColors[anomaly.severity] || ''}`}>
            {anomaly.severity}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
        {anomaly.location && (
          <span><strong>{t('report.location')}:</strong> {anomaly.location}</span>
        )}
      </div>
      {anomaly.differential?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <p className="text-[10px] text-muted-foreground mb-1 font-semibold">{t('report.differential')}:</p>
          <div className="flex flex-wrap gap-1">
            {anomaly.differential.map((d, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(210,14%,16%)] text-foreground/70">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const AnalysisReport = ({ report, t }) => {
  const hasComparison = report.comparison?.prior_available;

  return (
    <Tabs defaultValue="summary" className="w-full">
      <TabsList className="bg-[hsl(210,14%,12%)] mb-4 flex-wrap h-auto gap-1 p-1">
        <TabsTrigger value="summary" className="text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="tab-summary">
          <Shield size={12} className="mr-1" />
          {t('report.critical_findings')}
        </TabsTrigger>
        <TabsTrigger value="anomalies" className="text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="tab-anomalies">
          <Eye size={12} className="mr-1" />
          {t('report.anomalies')}
        </TabsTrigger>
        {hasComparison && (
          <TabsTrigger value="comparison" className="text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="tab-comparison">
            <GitCompareArrows size={12} className="mr-1" />
            {t('report.comparison')}
          </TabsTrigger>
        )}
        <TabsTrigger value="impression" className="text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="tab-impression">
          <Target size={12} className="mr-1" />
          {t('report.impression')}
        </TabsTrigger>
        <TabsTrigger value="recommendations" className="text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="tab-recommendations">
          <Stethoscope size={12} className="mr-1" />
          {t('report.recommendations')}
        </TabsTrigger>
        <TabsTrigger value="limitations" className="text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="tab-limitations">
          <Info size={12} className="mr-1" />
          {t('report.limitations')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="summary" className="space-y-4">
        <ReportSection
          title={t('report.critical_findings')}
          icon={<AlertTriangle size={16} className="text-red-400" />}
          items={report.critical_findings}
          emptyText={t('report.none_identified')}
          variant="critical"
        />
        <ReportSection
          title={t('report.complications')}
          icon={<Zap size={16} className="text-orange-400" />}
          items={report.time_sensitive_complications}
          emptyText={t('report.none_identified')}
          variant="high"
        />
        <ReportSection
          title={t('report.normal_structures')}
          icon={<CheckCircle size={16} className="text-green-400" />}
          items={report.normal_structures}
          emptyText="N/A"
          variant="normal"
        />
      </TabsContent>

      <TabsContent value="anomalies" className="space-y-3">
        {(report.key_anomalies || []).length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">
            {t('report.none_identified')}
          </p>
        ) : (
          report.key_anomalies.map((anomaly, i) => (
            <AnomalyCard key={i} anomaly={anomaly} t={t} />
          ))
        )}
      </TabsContent>

      {hasComparison && (
        <TabsContent value="comparison" className="space-y-4" data-testid="comparison-content">
          {report.comparison.evolution && (
            <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/15">
              <h4 className="text-xs font-semibold text-purple-300 mb-2 flex items-center gap-1.5">
                <GitCompareArrows size={13} />
                {t('report.evolution')}
              </h4>
              <p className="text-xs text-foreground/85 leading-relaxed">{report.comparison.evolution}</p>
            </div>
          )}
          {(report.comparison.changes || []).length > 0 && (
            <div className="space-y-1.5">
              <h4 className="text-xs font-semibold text-muted-foreground">{t('report.changes')}</h4>
              {report.comparison.changes.map((ch, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded bg-[hsl(210,14%,12%)]">
                  <span className="text-purple-400 mt-0.5">•</span>
                  <p className="text-xs text-foreground/80">{ch}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      )}

      <TabsContent value="impression" className="space-y-4">
        <div className="space-y-2">
          {(report.hierarchized_impression || []).map((imp, i) => (
            <div key={i} className="flex gap-3 p-3 rounded-lg bg-[hsl(210,14%,12%)]">
              <div className="w-6 h-6 rounded-full bg-cyan-500/15 text-cyan-400 flex items-center justify-center text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <p className="text-sm text-foreground/90">{imp}</p>
            </div>
          ))}
        </div>

        {report.confidence_calibration && (
          <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/15">
            <p className="text-[10px] text-cyan-400 uppercase tracking-wide font-semibold mb-1">
              {t('report.confidence')}
            </p>
            <p className="text-xs text-foreground/80">{report.confidence_calibration}</p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="recommendations" className="space-y-4">
        {report.recommendations?.immediate_actions?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-orange-400 mb-2 flex items-center gap-1">
              <Zap size={12} /> {t('report.immediate_actions')}
            </h4>
            <div className="space-y-1">
              {report.recommendations.immediate_actions.map((a, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded bg-orange-500/5">
                  <span className="text-orange-400 mt-0.5">•</span>
                  <p className="text-xs text-foreground/80">{a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {report.recommendations?.confirmatory_tests?.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-cyan-400 mb-2 flex items-center gap-1">
              <Stethoscope size={12} /> {t('report.confirmatory_tests')}
            </h4>
            <div className="space-y-1">
              {report.recommendations.confirmatory_tests.map((test, i) => (
                <div key={i} className="flex items-start gap-2 p-2 rounded bg-cyan-500/5">
                  <span className="text-cyan-400 mt-0.5">•</span>
                  <p className="text-xs text-foreground/80">{test}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {report.recommendations?.follow_up && (
          <div className="p-3 rounded-lg bg-[hsl(210,14%,12%)]">
            <h4 className="text-xs font-semibold text-muted-foreground mb-1">
              {t('report.follow_up')}
            </h4>
            <p className="text-xs text-foreground/80">{report.recommendations.follow_up}</p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="limitations" className="space-y-4" data-testid="report-limitations">
        <div className="space-y-2">
          {(report.limitations || []).map((lim, i) => (
            <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/10">
              <Info size={14} className="text-yellow-400 shrink-0 mt-0.5" />
              <p className="text-xs text-foreground/80">{lim}</p>
            </div>
          ))}
        </div>

        {report.disclaimer && (
          <div className="p-4 rounded-lg bg-[hsl(210,14%,12%)] border border-border">
            <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
              <Shield size={12} /> {t('report.disclaimer')}
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {report.disclaimer}
            </p>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
};

export default AnalysisReport;
